
import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';
import { FooterItem } from '../angular-core/components/navigation/footer-base/footer-base.component';
import { selectUserData } from '../store/selectors/auth.selectors';
import { OperationEnum } from '../utils/enums/operation.enums';
import { hasPermission, hasRoleUserData } from '../utils/functions/permission.function';
import { AllApiPermissions, IApiPermissions, IUserData } from '../utils/interfaces/auth.interfaces';
import { IRemoteXPACPortalState } from '../utils/interfaces/store.interfaces';
import { PermissionsService } from './permissions.service';
import { HeaderService } from '../angular-core/services/header.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class FooterItemsService {

  constructor(private store: Store<IRemoteXPACPortalState>, private permissionsService: PermissionsService, private header: HeaderService,) {
    this.initAuthInfo();
  }

  private userData: IUserData = null;

  private readonly allFooterItems: FooterItem[] = [
    { iconName: "dashboard", displayNameTranslation: "Modules.Dashboard.FooterItem", route: "dashboard" },
    { iconName: "article", displayNameTranslation: "Modules.Products.FooterItem", route: "articles", permission: "News", userRoles: ["CrossPoint", "Partner"] },
    { iconName: "construction", displayNameTranslation: "Modules.RemoteMaintenance.FooterItem", route: "service", permission: "Device.RemoteAccess", operationEnum: OperationEnum.ACTION, linkedRoutes: ['/xpac-management'] },
    { iconName: "inventory_2", displayNameTranslation: "Modules.InventoryManagement.FooterItem", route: "inventory-management", permission: "InventoryManagement" },
    { iconName: "sell", displayNameTranslation: "Modules.PriceList.FooterItem", route: "products", permission: "Pricelist" },
    { iconName: "description", displayNameTranslation: "Modules.Documents.FooterItem", route: "documents", permission: "Documents" },
    { iconName: "insights", displayNameTranslation: "Modules.Insights.FooterItem", route: "insights", permission: "Reports" },
    { iconName: "card_membership", displayNameTranslation: "Modules.Licenses.FooterItem", route: "licenses", permission: "RemoteMaintenance.License" },
    { iconName: "business", displayNameTranslation: "Modules.Companies.FooterItem", route: "companies", permission: "Companies" },
    { iconName: "person_search", displayNameTranslation: "Modules.Users.FooterItem", route: "users", permission: "Users" },
    { iconName: "account_circle", displayNameTranslation: "Modules.Users.MyAccount", route: "users/current", permission: "Users" },
    { iconName: "calculate", displayNameTranslation: "Modules.Argus.FooterItem", route: "argus-calculator", permission: "ArgusCalculator", },
    { iconName: "conveyor_belt", displayNameTranslation: "Modules.LogisticsComponent.FooterItem", route: "logistics", permission: "Device.Produced", },
    { iconName: "key", displayNameTranslation: "Modules.DevicePassword.FooterItem", route: "password", permission: "Device.SshPassword", },
    { iconName: "lock_open", displayNameTranslation: "Modules.DeviceUnlock.FooterItem", route: "unlock", permission: "Device.UnlockCode", },
    { iconName: "system_update_alt", displayNameTranslation: "Modules.Firmware.FooterItem", route: "firmware", permission: "Device.Firmware", operationEnum: OperationEnum.UPDATE, },
    { iconName: "construction", displayNameTranslation: "Modules.ProductionTooling.FooterItem", route: "production-tooling", permission: "Device.Configuration", operationEnum: OperationEnum.UPDATE, },
    { iconName: "people_alt", displayNameTranslation: "Modules.Impersonation.FooterItem", route: "impersonation", permission: "Impersonate", operationEnum: OperationEnum.ACTION, available: this.impersonationAvailable.bind(this) },
    { iconName: "admin_panel_settings", displayNameTranslation: "Modules.Preferences.FooterItem", route: "preferences" },
  ];

  private static readonly MAX_FOOTER_ITEMS_BEFORE_ADDING_MORE: number = 4;

  private getAllFooterItemsWithPermission(permissions: IApiPermissions): FooterItem[] {
    let footerItemsWithPermission: FooterItem[] = [];
    // First determine all items for which the user has a permission
    this.allFooterItems.forEach((footerItem: FooterItem) => {
      // Check the available hook first if it is present
      if (footerItem.available) {
        if (!footerItem.available(footerItem.route)) {
          return;
        }
      }
      // if permission is not set we just add it
      if (!footerItem.permission) {
        footerItemsWithPermission.push(footerItem);
      }
      else if (hasPermission(footerItem.permission as keyof AllApiPermissions, permissions, footerItem.operationEnum ? footerItem.operationEnum : OperationEnum.READ)) {
        if (footerItem.userRoles) {
          let hasRole: boolean = false;

          // Check if user has any role which is needed
          footerItem.userRoles.forEach((role: string) => {
            if (hasRoleUserData(role, this.userData)) {
              hasRole = true;
            }
          });

          if (hasRole) {
            footerItemsWithPermission.push(footerItem);
          }
        }
        else {
          footerItemsWithPermission.push(footerItem);
        }
      }
    });

    return footerItemsWithPermission;
  }

  private initAuthInfo(): void {
    this.store.select(selectUserData)
      .pipe(
        filter((value: IUserData | null) => value !== null),
        untilDestroyed(this)
      )
      .subscribe(async (userData: IUserData) => {
        this.userData = userData;
        this.setHeaderNavigateBackHook();
      });
  }

  private async setHeaderNavigateBackHook(): Promise<void> {
    const permissions: IApiPermissions = await this.permissionsService.getPermissions();
    if (permissions != null) {
      this.header.setNavigateBackHook(this.getBackNavigationPage.bind(this));
    }
  }

  private impersonationAvailable(): boolean {
    // Extra hook to block the item when we are already impersonating someone
    if (!this.userData) {
      return false;
    }
    if (this.userData.impersonator) {
      return false;
    }
    return true;
  }

  public async getFooterItemsFooter(): Promise<FooterItem[]> {
    const permissions: IApiPermissions = await this.permissionsService.getPermissions();
    if (permissions === null) {
      return [];
    }

    let footerItemsWithPermission: FooterItem[] = this.getAllFooterItemsWithPermission(permissions);

    // "More" should always be added because of the log out buttons present there, so add 1 to the total amount of footer items
    if ((footerItemsWithPermission.length + 1) > FooterItemsService.MAX_FOOTER_ITEMS_BEFORE_ADDING_MORE) {
      let footerItems: FooterItem[] = [];
      for (let i: number = 0; i < FooterItemsService.MAX_FOOTER_ITEMS_BEFORE_ADDING_MORE - 1; i++) {
        footerItems.push(footerItemsWithPermission[i]);
      }

      // Add a more button containing the other routes
      let linkedRoutes: string[] = [];
      for (let i: number = FooterItemsService.MAX_FOOTER_ITEMS_BEFORE_ADDING_MORE - 1; i < footerItemsWithPermission.length; i++) {
        linkedRoutes.push(`/${footerItemsWithPermission[i].route}`);
      }

      // More with its sub pages
      footerItems.push({ iconName: "more_horiz", displayNameTranslation: "Modules.More.FooterItem", route: "more", linkedRoutes: linkedRoutes });

      return footerItems;
    }
    else {
      // More without any sub pages
      footerItemsWithPermission.push({ iconName: "more_horiz", displayNameTranslation: "Modules.More.FooterItem", route: "more" });
      return footerItemsWithPermission;
    }
  }

  public getFooterItemsMore(permissions: IApiPermissions): FooterItem[] {
    let footerItemsWithPermission: FooterItem[] = this.getAllFooterItemsWithPermission(permissions);

    // Always include "More" in the count
    if ((footerItemsWithPermission.length + 1) > FooterItemsService.MAX_FOOTER_ITEMS_BEFORE_ADDING_MORE) {
      let footerItems: FooterItem[] = [];
      for (let i: number = FooterItemsService.MAX_FOOTER_ITEMS_BEFORE_ADDING_MORE - 1; i < footerItemsWithPermission.length; i++) {
        footerItems.push(footerItemsWithPermission[i]);
      }
      return footerItems;
    }
    else {
      return [];
    }
  }

  private getBackNavigationPage(route: string): string | null {
    let itemsInMorePage = this.getFooterItemsMore(this.permissionsService.getCachedPermissions());
    if (itemsInMorePage.find(item => item.route === route)) {
      return 'more';
    }
    return null;
  }
}